import React, { useState, useEffect } from 'react';
import { X, Settings, Save, Mail, MessageSquare } from 'lucide-react';
import { getVariables, getConditionOperators } from 'Services/storeService';
import logger from 'utils/logger';

// Audience routing applies to demo-related flows where messages target
// different recipient groups (guest, assigned host, ranked host fan-out,
// or the store admin).
const AUDIENCE_FLOW_CODES = new Set([
  'demo_request',
  'demo_schedule',
  'demo_reschedule',
  'demo_cancel',
  'demo_end',
  'demo_fallback_scheduled',
  'demo_fallback_asap',
]);

const AUDIENCE_OPTIONS_BY_FLOW = {
  demo_request: [
    { value: 'guest', label: 'Guest (the demo requester)' },
    { value: 'host', label: 'Host (all matched store hosts)' },
  ],
  demo_schedule: [
    { value: 'guest', label: 'Guest (scheduled attendee)' },
    { value: 'assigned_host', label: 'Assigned host (who accepted)' },
  ],
  demo_reschedule: [
    { value: 'guest', label: 'Guest (scheduled attendee)' },
    { value: 'assigned_host', label: 'Assigned host (who rescheduled)' },
  ],
  demo_cancel: [
    { value: 'guest', label: 'Guest (who was on the demo)' },
    { value: 'assigned_host', label: 'Assigned host (when the guest cancels an accepted demo)' },
    { value: 'host', label: 'Host (other ranked store hosts, on host cancel)' },
  ],
  demo_end: [
    { value: 'guest', label: 'Guests (everyone who joined the demo)' },
    { value: 'store_admin', label: 'Store admin' },
  ],
  demo_fallback_scheduled: [
    { value: 'guest', label: 'Guest (the demo attendee)' },
    { value: 'fallback_hosts', label: 'Fallback hosts (fallback + sales pool)' },
  ],
  demo_fallback_asap: [
    { value: 'guest', label: 'Guest (the demo requester)' },
    { value: 'fallback_hosts', label: 'Fallback hosts (fallback + sales pool)' },
  ],
};

// Operators that compare against a fixed truth/emptiness state need no
// comparison value input.
const VALUELESS_OPERATORS = new Set(['is_true', 'is_false', 'is_empty', 'is_not_empty']);

// `locked` disables the structural fields (DB-driven structure_locked flows);
// `readOnly` is the role gate — the caller passes `locked` too so every field
// is disabled, and this only changes the notice shown.
const ConfigPanel = ({ selectedNode, flowTypeCode, flowId, locked = false, readOnly = false, onClose, onUpdate, onOpenEmailEditor, onOpenSmsEditor }) => {
  const [formData, setFormData] = useState({});
  const [variables, setVariables] = useState([]);
  const [operators, setOperators] = useState([]);

  useEffect(() => {
    if (selectedNode) setFormData(selectedNode.data);
  }, [selectedNode]);

  // Condition nodes are configured from DB-driven lists: the flow's merge
  // variables and the condition operators table.
  const isConditionNode = selectedNode?.data?.nodeType === 'condition';
  useEffect(() => {
    if (!isConditionNode) return;
    let cancelled = false;
    const fetchOptions = async () => {
      try {
        const [variablesRes, operatorsRes] = await Promise.all([
          getVariables(flowId),
          getConditionOperators(),
        ]);
        if (cancelled) return;
        setVariables(variablesRes.data?.data || []);
        setOperators(operatorsRes.data?.data || []);
      } catch (err) {
        logger.error('Failed to load condition options', err);
      }
    };
    fetchOptions();
    return () => { cancelled = true; };
  }, [isConditionNode, flowId]);

  if (!selectedNode) return null;

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    onUpdate(selectedNode.id, formData);
    onClose();
  };

  const handleEditEmail = () => {
    // Do NOT call onUpdate here — committing config changes to flow state
    // right before opening the editor would trigger a spurious auto-save.
    // Config changes (label, subject) are only saved when the user clicks
    // "Save Changes" in the config panel itself.
    onClose();
    onOpenEmailEditor({ ...selectedNode, data: { ...selectedNode.data, ...formData } });
  };

  const handleEditSms = () => {
    onClose();
    onOpenSmsEditor({ ...selectedNode, data: { ...selectedNode.data, ...formData } });
  };

  const fieldStyle = {
    width: '100%', padding: '8px 12px',
    border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px',
  };

  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: '600',
    marginBottom: '8px', color: '#4a5568',
  };

  const renderAudienceField = () => {
    if (!AUDIENCE_FLOW_CODES.has(flowTypeCode)) return null;
    const options = AUDIENCE_OPTIONS_BY_FLOW[flowTypeCode] || AUDIENCE_OPTIONS_BY_FLOW.demo_request;
    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Audience</label>
        <select
          value={formData.audience || 'guest'}
          onChange={(e) => handleChange('audience', e.target.value)}
          style={fieldStyle}
          disabled={locked}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
          Assigned-host nodes only send when a host had accepted the demo.
        </div>
      </div>
    );
  };

  const renderFields = () => {
    const nodeType = formData.nodeType;

    switch (nodeType) {
      case 'trigger':
        return (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Trigger Name</label>
              <input type="text" value={formData.label || ''} onChange={(e) => handleChange('label', e.target.value)} style={fieldStyle} disabled={locked} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Condition</label>
              <input type="text" value={formData.subtitle || ''} onChange={(e) => handleChange('subtitle', e.target.value)} style={fieldStyle} disabled={locked} />
            </div>
          </>
        );

      case 'condition': {
        const selectedVariable = variables.find((v) => v.code === formData.variable);
        // Operators are DB-driven; narrow to the selected variable's data type
        // when known. `op.code` is the engine-facing operator (stored keys are
        // unique per data type); duplicate codes across data types collapse.
        const applicableOperators = [];
        const seenCodes = new Set();
        operators.forEach((op) => {
          if (selectedVariable && op.applicable_data_type !== selectedVariable.data_type) return;
          const code = op.code || op.key;
          if (seenCodes.has(code)) return;
          seenCodes.add(code);
          applicableOperators.push({ ...op, code });
        });
        const valueless = VALUELESS_OPERATORS.has(formData.operator);

        return (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Condition Name</label>
              <input type="text" value={formData.label || ''} onChange={(e) => handleChange('label', e.target.value)} style={fieldStyle} disabled={locked} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Description</label>
              <textarea
                value={formData.subtitle || ''}
                onChange={(e) => handleChange('subtitle', e.target.value)}
                style={{ ...fieldStyle, minHeight: '72px', resize: 'vertical' }}
                placeholder="Explain when this branch runs for store admins…"
                disabled={locked}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Variable</label>
              <select
                value={formData.variable || ''}
                onChange={(e) => handleChange('variable', e.target.value)}
                style={fieldStyle}
                disabled={locked}
              >
                <option value="">Select a variable…</option>
                {variables.map((v) => (
                  <option key={v.id} value={v.code}>
                    {v.description ? `${v.description} (${v.code})` : v.code}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Operator</label>
              <select
                value={formData.operator || ''}
                onChange={(e) => handleChange('operator', e.target.value)}
                style={fieldStyle}
                disabled={locked}
              >
                <option value="">Select an operator…</option>
                {applicableOperators.map((op) => (
                  <option key={op.id} value={op.code}>{op.label}</option>
                ))}
              </select>
            </div>
            {!valueless && (
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Value</label>
                <input
                  type="text"
                  value={formData.value || ''}
                  onChange={(e) => handleChange('value', e.target.value)}
                  style={fieldStyle}
                  placeholder="Comparison value"
                  disabled={locked}
                />
              </div>
            )}
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '-8px', marginBottom: '16px' }}>
              Nodes connected to the green handle run when the condition is met;
              nodes on the red handle run when it is not.
            </div>
          </>
        );
      }

      case 'wait':
        return (
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Duration</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                value={formData.duration || 0}
                onChange={(e) => {
                  handleChange('duration', e.target.value);
                  handleChange('label', `Wait ${e.target.value} ${formData.durationType || 'days'}`);
                }}
                style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                disabled={locked}
              />
              <select
                value={formData.durationType || 'days'}
                onChange={(e) => {
                  handleChange('durationType', e.target.value);
                  handleChange('label', `Wait ${formData.duration || 0} ${e.target.value}`);
                }}
                style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                disabled={locked}
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
              </select>
            </div>
          </div>
        );

      case 'email':
        return (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Email Name</label>
              <input type="text" value={formData.label || ''} onChange={(e) => handleChange('label', e.target.value)} style={fieldStyle} disabled={locked} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Subject</label>
              <input type="text" value={formData.subject || ''} onChange={(e) => handleChange('subject', e.target.value)} style={fieldStyle} disabled={locked} />
            </div>
            {renderAudienceField()}
            <div style={{ marginBottom: '16px' }}>
              <button
                onClick={handleEditEmail}
                style={{
                  width: '100%', padding: '12px 16px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white', border: 'none', borderRadius: '6px',
                  fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '8px', transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(102,126,234,0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <Mail size={18} /> {readOnly ? 'View Email Design' : 'Edit Email Design'}
              </button>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '8px', textAlign: 'center' }}>
                {readOnly
                  ? 'Open the email template in view-only mode'
                  : 'Open the drag & drop email editor to design your template'}
              </div>
            </div>
          </>
        );

      case 'sms':
        return (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>SMS Name</label>
              <input type="text" value={formData.label || ''} onChange={(e) => handleChange('label', e.target.value)} style={fieldStyle} disabled={locked} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Description</label>
              <textarea
                value={formData.subtitle || ''}
                onChange={(e) => handleChange('subtitle', e.target.value)}
                style={{ ...fieldStyle, minHeight: '72px', resize: 'vertical' }}
                placeholder="Explain when this branch runs for store admins…"
                disabled={locked}
              />
            </div>
            {renderAudienceField()}
            <div style={{ marginBottom: '16px' }}>
              <button
                onClick={handleEditSms}
                style={{
                  width: '100%', padding: '12px 16px',
                  background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                  color: 'white', border: 'none', borderRadius: '6px',
                  fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '8px', transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(72,187,120,0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <MessageSquare size={18} /> {readOnly ? 'View SMS Content' : 'Edit SMS Content'}
              </button>
            </div>
          </>
        );

      case 'demo_fallback_action':
        return (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Action Name</label>
              <input type="text" value={formData.label || ''} onChange={(e) => handleChange('label', e.target.value)} style={fieldStyle} disabled={locked} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Description</label>
              <textarea
                value={formData.subtitle || ''}
                onChange={(e) => handleChange('subtitle', e.target.value)}
                style={{ ...fieldStyle, minHeight: '72px', resize: 'vertical' }}
                disabled={locked}
              />
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>
              This node {formData.action_mode === 'asap_handoff'
                ? 'marks the ASAP demo as handed off to the fallback pool'
                : 'drops the absent host and re-opens the demo to the fallback pool'}.
              The action itself is fixed for this workflow.
            </div>
          </>
        );

      default:
        return (
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Label</label>
            <input type="text" value={formData.label || ''} onChange={(e) => handleChange('label', e.target.value)} style={fieldStyle} disabled={locked} />
          </div>
        );
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, width: '400px', height: '100vh',
      background: 'white', boxShadow: '-2px 0 8px rgba(0,0,0,0.1)', zIndex: 1000,
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Settings size={20} color="#4a5568" />
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1a202c' }}>Configure Node</h2>
        </div>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
          <X size={20} color="#a0aec0" />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
        {readOnly && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <span style={{ fontSize: '15px', lineHeight: '18px' }}>👁️</span>
            <div style={{ fontSize: '12px', color: '#475569', lineHeight: 1.5 }}>
              View only. Your role can review this step but not change it.
            </div>
          </div>
        )}
        {locked && !readOnly && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <span style={{ fontSize: '15px', lineHeight: '18px' }}>🔒</span>
            <div style={{ fontSize: '12px', color: '#92400e', lineHeight: 1.5 }}>
              This workflow's structure is locked. The steps and timing are fixed — you can
              edit the SMS message content only.
            </div>
          </div>
        )}
        {renderFields()}
      </div>

      <div style={{ padding: '20px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '12px' }}>
        <button onClick={onClose} style={{ flex: 1, padding: '10px 16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', color: '#4a5568' }}>
          {locked ? 'Close' : 'Cancel'}
        </button>
        {!locked && (
          <button onClick={handleSave} style={{ flex: 1, padding: '10px 16px', background: '#667eea', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Save size={16} /> Save Changes
          </button>
        )}
      </div>
    </div>
  );
};

export default ConfigPanel;
